import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';

import { IModeloReferencia, TipoOperacaoSocietario } from '../../../../hooks/useSocietario';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tipoOperacao: TipoOperacaoSocietario;
  modeloAtual?: IModeloReferencia | null;
  onSubmit: (data: {
    nome: string;
    corpo_referencia: string;
    instrucoes_globais: string | null;
    descricao: string | null;
  }) => Promise<void>;
  isLoading: boolean;
}

const tipoLabel: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração contratual',
  transformacao: 'Transformação (EI → LTDA)',
};

export const EditarModeloModal = ({
  isOpen,
  onClose,
  tipoOperacao,
  modeloAtual,
  onSubmit,
  isLoading,
}: Props) => {
  const [nome, setNome] = useState('');
  const [corpo, setCorpo] = useState('');
  const [instrucoes, setInstrucoes] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNome(modeloAtual?.nome ?? tipoLabel[tipoOperacao]);
      setCorpo(modeloAtual?.corpo_referencia ?? '');
      setInstrucoes(modeloAtual?.instrucoes_globais ?? '');
      setDescricao('');
    }
  }, [isOpen, modeloAtual, tipoOperacao]);

  const handleSalvar = async () => {
    await onSubmit({
      nome: nome.trim() || tipoLabel[tipoOperacao],
      corpo_referencia: corpo,
      instrucoes_globais: instrucoes.trim() || null,
      descricao: descricao.trim() || null,
    });
  };

  const podeSalvar = corpo.trim().length > 50;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Editar modelo de referência — {tipoLabel[tipoOperacao]}
          {modeloAtual && (
            <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
              Versão atual: <strong>{modeloAtual.versao}</strong>. Salvar criará uma nova versão e
              desativará esta.
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Nome</FormLabel>
              <Input
                size="sm"
                value={nome}
                onChange={e => setNome(e.target.value)}
                placeholder={tipoLabel[tipoOperacao]}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Descrição da versão (opcional)</FormLabel>
              <Input
                size="sm"
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                placeholder="Ex: ajuste no texto da cláusula 5 conforme revisão jurídica de 04/2026"
              />
              <FormHelperText fontSize="xs">
                Justificativa do que mudou nesta versão (auditoria).
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Instruções globais para a IA (opcional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={instrucoes}
                onChange={e => setInstrucoes(e.target.value)}
                placeholder="Ex: Sempre escreva em português brasileiro formal. Use ABNT na pontuação."
              />
              <FormHelperText fontSize="xs">
                Vai como prefixo do system prompt antes do corpo de referência.
              </FormHelperText>
            </FormControl>

            <FormControl isRequired isInvalid={!podeSalvar && corpo.length > 0}>
              <FormLabel fontSize="sm">Corpo de referência (markdown)</FormLabel>
              <Tabs size="sm" variant="enclosed">
                <TabList>
                  <Tab>Editor</Tab>
                  <Tab>Pré-visualização</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Textarea
                      fontFamily="monospace"
                      fontSize="xs"
                      rows={28}
                      value={corpo}
                      onChange={e => setCorpo(e.target.value)}
                      placeholder="# Cabeçalho do contrato&#10;&#10;CLÁUSULA PRIMEIRA - DA DENOMINAÇÃO&#10;A sociedade gira sob a denominação social de [RAZÃO SOCIAL DA SOCIEDADE]..."
                    />
                  </TabPanel>
                  <TabPanel px={0}>
                    <Box
                      as="pre"
                      whiteSpace="pre-wrap"
                      fontFamily="serif"
                      fontSize="sm"
                      p={4}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="gray.50"
                      maxH="600px"
                      overflowY="auto"
                    >
                      {corpo || (
                        <Text color="gray.500" fontStyle="italic">
                          (nada para pré-visualizar)
                        </Text>
                      )}
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
              <FormHelperText fontSize="xs">
                Texto fixo das cláusulas + placeholders descritivos para os campos variáveis (ex.:{' '}
                <code>[RAZÃO SOCIAL]</code>, <code>[QUALIFICAÇÃO COMPLETA DE CADA SÓCIO]</code>).
                Mínimo 50 caracteres.
              </FormHelperText>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSalvar}
            isLoading={isLoading}
            isDisabled={!podeSalvar || isLoading}
          >
            Salvar nova versão
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditarModeloModal;
